
import React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import NavbarMenu from "./NavbarMenu";
import NavbarActions from "./NavbarActions";

interface MobileNavbarProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileNavbar: React.FC<MobileNavbarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Mobile Navigation Menu - Enterprise-level animation and design */}
      <div 
        className={cn(
          "fixed inset-y-0 right-0 transform w-full sm:w-4/5 max-w-sm z-[60] overflow-hidden md:hidden",
          "bg-gradient-to-br from-propmatch-dark/98 via-propmatch-dark/95 to-black/98",
          "backdrop-blur-2xl border-l border-white/10 shadow-2xl",
          "transition-all duration-500 ease-out will-change-transform",
          "flex flex-col",
          isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        )}
        id="mobile-menu"
        aria-hidden={!isOpen}
        role="dialog"
        aria-labelledby="mobile-menu-title"
      >
        {/* Header with close button */}
        <div className="relative p-4 border-b border-white/10 bg-gradient-to-r from-propmatch-blue/10 to-transparent">
          <h2 id="mobile-menu-title" className="sr-only">{t("nav.mobileMenu", "Navigation Menu")}</h2>
          
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-br from-propmatch-blue/5 to-transparent pointer-events-none" />
          
          <div className="relative flex justify-between items-center z-10">
            <div className="text-white font-semibold text-base">
              {t("nav.menu", "Menu")}
            </div>
            
            <button 
              onClick={onClose} 
              className={cn(
                "relative p-2 rounded-xl transition-all duration-300 ease-out touch-manipulation z-20",
                "bg-white/5 hover:bg-white/10 active:bg-white/15",
                "border border-white/10 hover:border-white/20",
                "shadow-lg hover:shadow-xl active:scale-95",
                "cursor-pointer"
              )}
              aria-label={t("nav.closeMenu", "Close menu")}
              role="button"
              type="button"
            >
              <X size={20} className="text-white drop-shadow-sm" />
              
              {/* Ripple effect */}
              <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-white/10 scale-0 rounded-xl transition-transform duration-300 group-active:scale-150" />
              </div>
            </button>
          </div>
        </div>
        
        {/* Menu content with staggered animations */}
        <div 
          className={cn(
            "flex-1 overflow-y-auto px-4 py-6 transition-all duration-700 ease-out",
            isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{
            transitionDelay: isOpen ? '200ms' : '0ms'
          }}
        >
          <NavbarMenu closeMenu={onClose} isMobile={true} />
        </div>
        
        {/* Sticky bottom actions for mobile with enterprise styling */}
        <div 
          className={cn(
            "border-t border-white/10 bg-gradient-to-t from-black/20 to-transparent",
            "transition-all duration-700 ease-out",
            isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{
            transitionDelay: isOpen ? '400ms' : '0ms'
          }}
        >
          <NavbarActions closeMenu={onClose} isMobile={true} />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-propmatch-blue/10 to-transparent opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-radial from-propmatch-blue-light/10 to-transparent opacity-30 pointer-events-none" />
      </div>
      
      {/* Overlay for mobile menu - Enhanced with blur effect */}
      {isOpen && (
        <div 
          className={cn(
            "fixed inset-0 z-[55] md:hidden",
            "bg-black/60 backdrop-blur-sm",
            "transition-all duration-500 ease-out",
            "animate-fade-in"
          )}
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          aria-hidden="true"
          role="button"
          tabIndex={0}
        />
      )}
    </>
  );
};

export default MobileNavbar;
